import React from 'react';
import GridContainer from '../../../../Grid/GridContainer';
import GridItem from '../../../../Grid/GridItem';
import CustomInput from '../../../../CustomInput/CustomInput';
import ValidationInput from '../../../../ValidationInput';
import DateInput from '../../../../DateInput';
import TimePickerGCalendar from '../../../../TimePickerGCalendar';
import SelectInput from '../../../../SelectInput';
import { convertTo12HourFormat } from '../../../../../util/dateUtils/dateFormatters';

interface AddressValidationInputProps {
  text: string;
  isOneWay: boolean;
  event: any;
  appointment: any;
  formatPatientAddress: (customer: any) => string;
  type: 'source' | 'destination';
}

export const AddressValidationInput: React.FC<AddressValidationInputProps> = ({
  text,
  isOneWay,
  event,
  appointment,
  formatPatientAddress,
  type
}) => {
  const getValue = () => {
    const hospitalAddress = event?.origin?.transferItemRequestId?.hospital?.businessName
      ? `${event.origin.transferItemRequestId.hospital.businessName} - ${event?.origin?.transferItemRequestId?.hospital?.address}`
      : "-";
    const patientAddress = formatPatientAddress(appointment?.customer);

    if (isOneWay) {
      // Para viajes de ida: origen = paciente, destino = hospital
      return type === 'source' ? patientAddress : hospitalAddress;
    } else {
      // Para viajes de vuelta: origen = hospital, destino = paciente
      return type === 'source' ? hospitalAddress : patientAddress;
    }
  };

  return (
    <GridItem xs={12} sm={12} md={12} className="no-padding">
      <ValidationInput
        text={text}
        elements={isOneWay ? undefined : ""}
        disabled={true}
        inputProps={isOneWay ? { style: { width: "100%" } } : undefined}
        value={getValue()}
      />
    </GridItem>
  );
};

const TransferServiceInfoEdit = ({
  event,
  appointment,
  formatTransferTypeRecive,
  formatDistance,
  translateStatus,
  formatPatientAddress,
  getTransferWayLabel,
  isOneWay,
  t,
  state,
  handleValue,
  drivers,
  handleTimeChange,
  isEditable,
  dates
}: any) => {

  const driverOptions = (drivers ?? []).map((d: any) => {
    return {
      id: d?.id,
      value: `${d?.lastName} ${d?.firstName}`
    };
  });

  return (
    <div style={{ width: '100%' }}>
      <GridContainer>
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            labelText={t('service.type')}
            disabled={true}
            value={getTransferWayLabel()}
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            labelText={t("service.traslado")}
            disabled={true}
            value={formatTransferTypeRecive(event?.origin?.transferItemRequestId?.transferType)}
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            labelText={t("reassignAppointments.table.transferId")}
            disabled={true}
            value={event?.id ?? "-"}
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6} className="no-padding">
          <SelectInput
            label={t("common.driver")}
            elements={driverOptions}
            disabled={(!isEditable())}
            onSelectedValue={(value: string | number) => {
              const selectedDriver = (drivers ?? []).find((d: any) => (d.personId ?? d.id) === value);
              if (selectedDriver) {
                handleValue("driver", {
                  personId: selectedDriver.personId ?? selectedDriver.id,
                  firstName: selectedDriver.firstName ?? selectedDriver.nombre ?? '',
                  lastName: selectedDriver.lastName ?? selectedDriver.apellido ?? '',
                });
              } else {
                handleValue("driver", null);
              }
            }}
            value={state.driver?.personId ?? state.driver?.id ?? state.driver}
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6} className="no-padding">
          <DateInput
            text={t('label.startDate')}
            disabled={!isEditable()}
            onChangeValue={(value: string) => {
              handleValue("startDate", value);
            }}
            value={state.startDate}
            key={event?.origin?.transferScheduleId}
            min={dates?.startDate}
            max={dates?.endDate}
            minDate={true}
            maxDate={true}
          />
        </GridItem>

        <AddressValidationInput
          text={t('service.source.address')}
          isOneWay={isOneWay}
          event={event}
          appointment={appointment}
          formatPatientAddress={formatPatientAddress}
          type="source"
        />

        {isOneWay ?
                <GridItem xs={6} sm={6} md={6}>
                <CustomInput
                  labelText={t('service.stimated.pickup.time')}
                  disabled={true}
                  value={convertTo12HourFormat(state.startTime)}
                />
              </GridItem> : 
               <GridItem xs={6} sm={6} md={6} className="no-padding">
               <TimePickerGCalendar
                 text={t('service.stimated.pickup.time')}
                 value={convertTo12HourFormat(state.startTime)}
                 name="edit_pickup_time"
                 disabled={!isEditable()}
                 onSelectedValue={(value: string) => {
                   handleTimeChange(value, 'edit_pickup_time');
                 }}
                 invalid={!state.startTime}
               />
             </GridItem>}

        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            labelText={t('service.real.pickup.time')}
            disabled={true}
            value={
              (event?.origin?.transferScheduleStatus === 'DONE' || event?.origin?.transferScheduleStatus === 'ENDED')
                ? convertTo12HourFormat(event?.origin?.startDateTime)
                : '-'
            }
          />
        </GridItem>
        {isOneWay ? (
          <AddressValidationInput
            text={t('service.institution.destination')}
            isOneWay={isOneWay}
            event={event}
            appointment={appointment}
            formatPatientAddress={formatPatientAddress}
            type="destination"
          />
        ) : (
          <AddressValidationInput
            text={t('service.address')}
            isOneWay={isOneWay}
            event={event}
            appointment={appointment}
            formatPatientAddress={formatPatientAddress}
            type="destination"
          />
        )}
    <GridItem xs={6} sm={6} md={6} className="no-padding">
      <TimePickerGCalendar
        text={t('service.stimated.arrival.time')}
        value={convertTo12HourFormat(state.time_end_suggested)}
        name="edit_arrival_time"
        disabled={!isEditable() || !isOneWay}
        onSelectedValue={(value: string) => {
          handleTimeChange(value, 'edit_arrival_time');
        }}
        invalid={!state.time_end_suggested}
      />
    </GridItem>
       
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            type="text"
            disabled={true}
            labelText={t('service.real.arrival.time')}
            value={
              (event?.origin?.transferScheduleStatus === 'DONE' || event?.origin?.transferScheduleStatus === 'ENDED')
                ? convertTo12HourFormat(event?.origin?.endDateTime ?? event?.transferScheduleEndDateTime)
                : '-'
            }
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            type="text"
            disabled={true}
            labelText={t('service.estimated.distance')}
            value={formatDistance(event?.origin?.geoDistance, true)}
          />
        </GridItem>
        <GridItem xs={6} sm={6} md={6}>
          <CustomInput
            type="text"
            disabled={true}
            labelText={t('service.real.distance')}
            value={formatDistance(event?.origin?.geoTrackDistance, true)}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={12} className="no-padding">
          <ValidationInput
            type="text"
            disabled={true}
            inputProps={{ style: { width: "100%" } }}
            text={t('status')}
            value={translateStatus(event?.origin?.transferScheduleStatus)}
          />
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default TransferServiceInfoEdit; 